import React, { useEffect, useState } from 'react';
import { FormSelector } from './FormSelector';
import { styled } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Paper, { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import { InsuranceForm } from '../InsuranceForm/InsuranceForm';
import { PatientForm } from '../PatientForm/PatientForm';
import { usePartnerGetPatientInsurance } from 'hooks/requests';
import { MenuItem } from '@mui/material';
import { useSnackbar } from 'notistack';
import Divider from '@mui/material/Divider';
import { PatientInsuranceInfo } from 'types/patientInsurance';

export interface PatientInsuranceDialogProps {
  invoiceNo?: number;
  srcSystemKey?: number;
  open?: boolean;
  close: () => void;
}

export const PatientInsuranceDialog: React.FC<PatientInsuranceDialogProps> = ({
  invoiceNo,
  srcSystemKey,
  open = true,
  close = () => { }
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    mutate: getPatientInformationMutation,
    data = {} as unknown as PatientInsuranceInfo
  } = usePartnerGetPatientInsurance();

  const [form, setForm] = useState(FORM_SELECTION.PATIENT);

  const handleError = () => {
    enqueueSnackbar('An Error has occurred to load up the data.', {
      variant: 'error'
    });
  };

  useEffect(() => {
    if (!(invoiceNo && srcSystemKey)) return;
    getPatientInformationMutation(
      {
        account: invoiceNo,
        srcSystemKey
      },
      { onError: handleError }
    );
  }, [invoiceNo, srcSystemKey]);

  useEffect(() => {
    if (open) return;
    setForm(FORM_SELECTION.PATIENT);
  }, [open]);

  return (
    <StyledDialog
      open={open}
      PaperComponent={PaperComponent}
      maxWidth="lg"
      aria-labelledby="details"
    >
      <StyledDialogTitle
        style={{
          zIndex: 20000
        }}
        id="details"
      >
        <TitleControl>
          <Typography variant="h6">{`Information Update Request for Invoice: ${invoiceNo}`}</Typography>
          <CloseIcon style={{ cursor: 'pointer' }} onClick={() => close()} />
        </TitleControl>
      </StyledDialogTitle>
      <p
        style={{
          margin: '0 24px 12px 24px',
          lineHeight: 1.5
        }}
      >
        Please submit your requested changes in the sections below. NOTE: Data
        displayed could be up to 5 business days old.
      </p>
      <div
        style={{
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <h3
          style={{
            margin: '0 0 0 24px'
          }}
        >
          Select Update Request Type:
        </h3>
        <FormSelector
          value={form}
          onChange={(e: { target: { value: string } }) =>
            setForm(e.target.value)
          }
          style={{
            margin: '6px 24px 12px 24px',
            maxWidth: '300px',
            width: '100%'
          }}
        >
          {Object.keys(FORM_SELECTION).map(formKey => {
            const formValue =
              FORM_SELECTION[formKey as keyof typeof FORM_SELECTION];
            return (
              <MenuItem
                key={formKey}
                value={formValue}
                style={{ height: '36px' }}
                data-cy={`${formKey}-form-option`}
              >
                {formValue}
              </MenuItem>
            );
          })}
        </FormSelector>
      </div>
      <Divider
        style={{
          margin: '0px 24px 16px 24px'
        }}
      />
      <div
        style={{
          maxWidth: '1000px'
        }}
      >
        {FORM_SELECTION.PATIENT === form ? (
          <PatientForm
            initialValues={data.patientInformation || {}}
            data={data}
            setSnackbarState={(state) => {
              if (state.message) {
                enqueueSnackbar(state.message, {
                  variant: state.severity || 'error'
                });
              }
            }}
          />
        ) : null}
        {FORM_SELECTION.PRIMARY === form ? (
          <InsuranceForm
            ordinal="primary"
            initialValues={data.primaryInsurance || {}}
            data={data}
            setSnackbarState={(state) => {
              if (state.message) {
                enqueueSnackbar(state.message, {
                  variant: state.severity || 'error'
                });
              }
            }}
          />
        ) : null}
        {FORM_SELECTION.SECONDARY === form ? (
          <InsuranceForm
            ordinal="secondary"
            initialValues={data.secondaryInsurance || {}}
            data={data}
            setSnackbarState={(state) => {
              if (state.message) {
                enqueueSnackbar(state.message, {
                  variant: state.severity || 'error'
                });
              }
            }}
          />
        ) : null}
      </div>
    </StyledDialog>
  );
};

function PaperComponent(props: PaperProps) {
  return (
    <Draggable handle="#details" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

const StyledDialogTitle = styled(DialogTitle)`
  cursor: move;
  position: sticky;
  top: 0;
  background-color: #f5f5f5;
`;
const TitleControl = styled('div')`
  display: flex;
  justify-content: space-between;
`;
const StyledDialog = styled(Dialog)`
  .MuiPaper-root.MuiDialog-paper {
    background-color: #f5f5f5;
    border: 6px solid #95bcd7;
  }
`;

const FORM_SELECTION = {
  PATIENT: 'PATIENT',
  PRIMARY: 'PRIMARY INSURANCE',
  SECONDARY: 'SECONDARY INSURANCE'
};
